import React, { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useNavigate } from "react-router-dom";
import ProductLoader from "../Common/ProductLoader";
import { getBanners } from "../../Utils/api-service";
import "./BannerCarousel.css";

const AUTOPLAY_DELAY_MS = 2500;
/** Embla only loops when slides fill the viewport — duplicate until they do. */
const MIN_LOOP_SLIDES = 6;

function normalizeBanners(rows) {
  if (!Array.isArray(rows)) return [];

  return rows
    .filter((row) => row?.imageUrl)
    .map((row, index) => ({
      id: row.id ?? `banner-${index}`,
      imageUrl: row.imageUrl,
      title: row.title,
      link: row.link,
    }));
}

function buildLoopSlides(banners) {
  const copies = Math.max(2, Math.ceil(MIN_LOOP_SLIDES / banners.length));
  const slides = [];

  for (let copy = 0; copy < copies; copy += 1) {
    banners.forEach((banner, index) => {
      slides.push({
        ...banner,
        slideKey: `${banner.id}__${copy}__${index}`,
      });
    });
  }

  return slides;
}

function BannerSlide({ banner, onClick }) {
  const isInteractive = Boolean(banner.link || onClick);

  const handleClick = () => onClick?.(banner);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div className="banner-carousel__slide">
      <div
        className={`banner-carousel__card${
          isInteractive ? " banner-carousel__card--interactive" : ""
        }`}
        onClick={isInteractive ? handleClick : undefined}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        aria-label={banner.title || "Promotional banner"}
      >
        <img
          src={banner.imageUrl}
          alt={banner.title || ""}
          className="banner-carousel__image"
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      </div>
    </div>
  );
}

function BannerCarouselTrack({ banners, onBannerClick, autoplayEnabled }) {
  const loopSlides = useMemo(() => buildLoopSlides(banners), [banners]);

  const plugins = useMemo(
    () =>
      autoplayEnabled
        ? [
            Autoplay({
              delay: AUTOPLAY_DELAY_MS,
              stopOnInteraction: false,
            }),
          ]
        : [],
    [autoplayEnabled],
  );

  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      containScroll: false,
      duration: 25,
    },
    plugins,
  );

  return (
    <div className="banner-carousel__viewport" ref={emblaRef}>
      <div className="banner-carousel__container">
        {loopSlides.map((banner) => (
          <BannerSlide
            key={banner.slideKey}
            banner={banner}
            onClick={onBannerClick}
          />
        ))}
      </div>
    </div>
  );
}

function BannerCarousel({
  banners: bannersProp,
  loading: loadingProp,
  autoFetch = true,
  autoplay = true,
  className = "",
  onBannerClick,
}) {
  const navigate = useNavigate();
  const isControlled = bannersProp !== undefined;
  const [internalBanners, setInternalBanners] = useState([]);
  const [internalLoading, setInternalLoading] = useState(
    !isControlled && autoFetch,
  );
  const [reducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );

  const banners = useMemo(
    () => (isControlled ? normalizeBanners(bannersProp) : internalBanners),
    [bannersProp, internalBanners, isControlled],
  );

  const loading = isControlled ? Boolean(loadingProp) : internalLoading;

  useEffect(() => {
    if (isControlled || !autoFetch) return undefined;

    let isMounted = true;

    getBanners()
      .then((response) => {
        if (!isMounted) return;
        setInternalBanners(
          response?.status === 200
            ? normalizeBanners(response?.data?.rows)
            : [],
        );
      })
      .catch((error) => {
        console.error("Error fetching banners:", error);
        if (isMounted) setInternalBanners([]);
      })
      .finally(() => {
        if (isMounted) setInternalLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [autoFetch, isControlled]);

  const handleBannerClick = useCallback(
    (banner) => {
      if (onBannerClick) {
        onBannerClick(banner);
        return;
      }
      if (!banner.link) return;

      try {
        const url = new URL(banner.link, window.location.origin);
        navigate(url.pathname + url.search);
      } catch {
        window.location.href = banner.link;
      }
    },
    [navigate, onBannerClick],
  );

  const rootClassName = ["banner-carousel", className]
    .filter(Boolean)
    .join(" ");

  if (loading) {
    return (
      <section
        className={rootClassName}
        aria-busy="true"
        aria-label="Loading banners"
      >
        <div className="banner-carousel__loader">
          <ProductLoader />
        </div>
      </section>
    );
  }

  if (!banners.length) return null;

  if (banners.length === 1) {
    return (
      <section className={rootClassName} aria-label="Promotional banners">
        <div className="banner-carousel__track banner-carousel__track--single">
          <BannerSlide banner={banners[0]} onClick={handleBannerClick} />
        </div>
      </section>
    );
  }

  return (
    <section
      className={`${rootClassName} banner-carousel--loop banner-pt-0 banner-pb-0`}
      aria-label="Promotional banners"
    >
      <BannerCarouselTrack
        banners={banners}
        onBannerClick={handleBannerClick}
        autoplayEnabled={autoplay && !reducedMotion}
      />
    </section>
  );
}

export default BannerCarousel;
